import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { compact, find, join, last, lowerCase, map, orderBy, range, size, sortBy, split, truncate, uniq } from 'lodash';
import React from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';

import { updateNeighborhoodClustersJobInternalApproval } from 'api/jobs';
import { borderColorPalette, colorPalette } from 'components/FeaturesDashboard/chartsColorPallete';
import { getColorForCellClassificationLayer } from 'components/Procedure/Infobar/slidesVisualizationAndConfiguration/helpers';
import { CellColorMapping } from 'interfaces/cellColorMapping';
import { ClusteringResult, JobType, NeighborhoodClustersJob } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { Taxonomy } from 'interfaces/taxonomy';
import { formatNumber } from 'utils/helpers';
import { getJobQueryKey } from 'utils/queryHooks/jobs/useJob';
import { jobsQueryKey } from 'utils/queryHooks/jobs/useJobs';
import useTaxonomy, { cellTaxonomy } from 'utils/queryHooks/taxonomy/useTaxonomy';
import { useCellColorMappings } from 'utils/queryHooks/useCellColorMappings';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';
import JobReportTab from './JobReportTab';
import {
  formatCellNeighborhoodMatrixValue,
  getCellNeighborhoodColumnLabels,
  summarizeCellNeighborhoodJob,
} from './cellNeighborhoodsSummary';

interface Props {
  pageSize?: number;
  selectedStudyId?: string;
}

ChartJS.register(ArcElement, BarElement, CategoryScale, ChartDataLabels, Legend, LinearScale, Tooltip);

const neighborhoodJobTypes = [JobType.MultiplexNeighborhoodClusters, JobType.NeighborhoodClusters];

const getClusterPopulationPercentageValue = (fraction: number) => fraction * 100;

const formatClusterPopulationPercentage = (percentageValue: number) => `${formatNumber(percentageValue, '0.00')}%`;

const clusterPopulationPieOptions: ChartOptions<'doughnut'> = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '48%',
  plugins: {
    datalabels: {
      display: true,
      formatter: (value: number) => formatClusterPopulationPercentage(value),
    },
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'doughnut'>) => {
          const value = typeof context.parsed === 'number' ? context.parsed : 0;
          return `${context.label}: ${formatClusterPopulationPercentage(value)}`;
        },
      },
    },
  },
};

interface ClusterPopulationEntry {
  backgroundColor: string;
  borderColor: string;
  label: string;
  value: number;
}

interface ClusterCentroidEntry {
  backgroundColor: string;
  borderColor: string;
  label: string;
  value: number;
}

type ChartColorPair = {
  backgroundColor: string;
  borderColor: string;
};

type CellClassChartColorResolver = (label: string, columnIndex: number) => ChartColorPair;

const SummaryMetric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  return (
    <Grid item xs={12} sm={4}>
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4">{value}</Typography>
      </Paper>
    </Grid>
  );
};

const KeyValueDetails: React.FC<{
  details: Array<{ label: string; value: string | number }>;
  title: string;
}> = ({ details, title }) => {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Grid container direction="column" spacing={1}>
        {map(details, ({ label, value }) => (
          <Grid item key={label}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

const MatrixTable: React.FC<{
  columnLabels: string[];
  rows: number[][];
  title: string;
  valueMode: 'fraction' | 'number';
}> = ({ columnLabels, rows, title, valueMode }) => {
  if (!size(rows)) {
    return null;
  }

  return (
    <Grid item xs={12} lg={6}>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Typography variant="subtitle2" sx={{ px: 2, pt: 2 }}>
          {title}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cluster</TableCell>
                {map(columnLabels, (columnLabel) => (
                  <TableCell key={columnLabel}>{columnLabel}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {map(rows, (row, rowIndex) => (
                <TableRow key={`${title}-${rowIndex}`}>
                  <TableCell>{`Cluster ${rowIndex + 1}`}</TableCell>
                  {map(row, (value, columnIndex) => (
                    <TableCell key={`${title}-${rowIndex}-${columnIndex}`}>
                      {formatCellNeighborhoodMatrixValue(value, valueMode)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Grid>
  );
};

const getKValueLabel = ({ kMeansParams }: ClusteringResult) => {
  if (kMeansParams.k) {
    return `K=${kMeansParams.k}`;
  }

  return `K=${kMeansParams.minK}-${kMeansParams.maxK}`;
};

const doesCellColorMappingMatchLayerName = (cellColorMapping: CellColorMapping, layerName: string) => {
  const sortedTags = sortBy(cellColorMapping.tags);
  const tagsString = join(sortedTags, '-');
  const cellType = last(split(cellColorMapping?.cellClassId, '.'));
  const layerNameFromCell = `${cellType}${tagsString ? `-${tagsString}` : ''}`;
  const tagsFromLayerName = split(layerName, '-').slice(1);
  const sortedLayerName = `${split(layerName, '-')[0]}${size(tagsFromLayerName) ? '-' : ''}${join(
    sortBy(tagsFromLayerName),
    '-'
  )}`;

  return lowerCase(sortedLayerName) === lowerCase(layerNameFromCell);
};

const getCellClassLayerNameCandidates = (label: string, cellTaxonomies?: Taxonomy[]) => {
  const [baseLabel, ...tags] = split(label, '-');
  const tagSuffix = size(tags) ? `-${join(tags, '-')}` : '';
  const matchingTaxonomy = find(cellTaxonomies, (taxonomy) => {
    const taxonomyTail = last(split(taxonomy.path, '.')) || '';

    return compact([taxonomy.name, taxonomy.path, taxonomyTail]).some(
      (candidate) => lowerCase(candidate) === lowerCase(baseLabel)
    );
  });

  return uniq(
    compact([
      label,
      matchingTaxonomy?.path,
      matchingTaxonomy?.path ? `${matchingTaxonomy.path}${tagSuffix}` : undefined,
      matchingTaxonomy ? `${last(split(matchingTaxonomy.path, '.'))}${tagSuffix}` : undefined,
    ])
  );
};

const getStudyCellClassColor = ({
  cellColorMappings,
  cellTaxonomies,
  label,
}: {
  cellColorMappings?: CellColorMapping[];
  cellTaxonomies?: Taxonomy[];
  label: string;
}): string | undefined => {
  const layerNameCandidates = getCellClassLayerNameCandidates(label, cellTaxonomies);

  const colorFromResolver = find(
    map(layerNameCandidates, (layerName) =>
      getColorForCellClassificationLayer({
        layerName,
        taxonomies: cellTaxonomies,
        cellColorMappings,
        stainTypeId: '',
      })
    ),
    (resolvedColor): resolvedColor is string => Boolean(resolvedColor)
  );

  if (colorFromResolver) {
    return colorFromResolver;
  }

  return find(cellColorMappings, (cellColorMapping) =>
    layerNameCandidates.some((layerName) => doesCellColorMappingMatchLayerName(cellColorMapping, layerName))
  )?.color;
};

const getDefaultChartColorsForLabel = (columnIndex: number): ChartColorPair => ({
  backgroundColor: colorPalette[columnIndex % colorPalette.length],
  borderColor: borderColorPalette[columnIndex % borderColorPalette.length],
});

const getChartColorsForLabel = ({
  cellColorMappings,
  cellTaxonomies,
  columnIndex,
  label,
}: {
  cellColorMappings?: CellColorMapping[];
  cellTaxonomies?: Taxonomy[];
  columnIndex: number;
  label: string;
}): ChartColorPair => {
  const mappedColor = getStudyCellClassColor({ cellColorMappings, cellTaxonomies, label });

  if (!mappedColor) {
    return getDefaultChartColorsForLabel(columnIndex);
  }

  return {
    backgroundColor: mappedColor,
    borderColor: darken(mappedColor, 0.2),
  };
};

const getClusterPopulationEntries = (
  clusterFractions: number[],
  columnLabels: string[],
  resolveCellClassChartColor: CellClassChartColorResolver
): ClusterPopulationEntry[] => {
  return orderBy(
    compact(
      map(columnLabels, (columnLabel, columnIndex) => {
        const value = clusterFractions[columnIndex] || 0;

        if (value <= 0) {
          return null;
        }

        const { backgroundColor, borderColor } = resolveCellClassChartColor(columnLabel, columnIndex);

        return {
          backgroundColor,
          borderColor,
          label: columnLabel,
          value,
        };
      })
    ),
    ['value', 'label'],
    ['desc', 'asc']
  );
};

const getClusterPopulationChartData = (
  clusterLabel: string,
  entries: ClusterPopulationEntry[]
): ChartData<'doughnut'> => {
  return {
    datasets: [
      {
        backgroundColor: map(entries, 'backgroundColor'),
        borderColor: map(entries, 'borderColor'),
        borderWidth: 1.5,
        data: map(entries, ({ value }) => getClusterPopulationPercentageValue(value)),
        label: clusterLabel,
      },
    ],
    labels: map(entries, 'label'),
  };
};

const formatCentroidValue = (value: number) => formatNumber(value);

const centroidBarChartOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y',
  plugins: {
    datalabels: {
      align: 'end',
      anchor: 'end',
      color: 'inherit',
      formatter: (value: number) => formatCentroidValue(value),
    },
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<'bar'>) => {
          const parsedValue = typeof context.parsed === 'number' ? context.parsed : context.parsed?.x;

          return `${context.label}: ${formatCentroidValue(typeof parsedValue === 'number' ? parsedValue : 0)}`;
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        callback: (value) => formatCentroidValue(Number(value)),
      },
      title: {
        display: true,
        text: 'Centroid value',
      },
    },
    y: {
      ticks: {
        callback: function (value: any) {
          const label = this.getLabelForValue(value);

          return truncate(label, { length: 32 });
        },
      },
    },
  },
};

const getClusterCentroidEntries = (
  centroidValues: number[],
  columnLabels: string[],
  resolveCellClassChartColor: CellClassChartColorResolver
): ClusterCentroidEntry[] => {
  return map(columnLabels, (columnLabel, columnIndex) => ({
    ...resolveCellClassChartColor(columnLabel, columnIndex),
    label: columnLabel,
    value: centroidValues[columnIndex] || 0,
  }));
};

const getCentroidChartData = (clusterLabel: string, entries: ClusterCentroidEntry[]): ChartData<'bar'> => {
  return {
    datasets: [
      {
        backgroundColor: map(entries, 'backgroundColor'),
        borderColor: map(entries, 'borderColor'),
        borderWidth: 1.5,
        data: map(entries, 'value'),
        label: clusterLabel,
      },
    ],
    labels: map(entries, 'label'),
  };
};

const ClusterPopulationPanel: React.FC<{
  clusterFractions: number[];
  clusterLabel: string;
  columnLabels: string[];
  resolveCellClassChartColor: CellClassChartColorResolver;
}> = ({ clusterFractions, clusterLabel, columnLabels, resolveCellClassChartColor }) => {
  const entries = getClusterPopulationEntries(clusterFractions, columnLabels, resolveCellClassChartColor);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        height: '100%',
        p: 2,
      }}
    >
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Population mix</Typography>
          <Typography variant="body2" color="text.secondary">
            Cell-label contribution inside {clusterLabel.toLowerCase()}.
          </Typography>
        </Stack>
        {size(entries) ? (
          <>
            <Box sx={{ height: 220, position: 'relative' }}>
              <Doughnut
                data={getClusterPopulationChartData(clusterLabel, entries)}
                options={clusterPopulationPieOptions}
              />
            </Box>
            <Stack spacing={1}>
              {map(entries, ({ backgroundColor, borderColor, label, value }) => (
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  key={`${clusterLabel}-${label}`}
                  spacing={2}
                >
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <Box
                      sx={{
                        backgroundColor,
                        border: `1px solid ${borderColor}`,
                        borderRadius: '50%',
                        flexShrink: 0,
                        height: 12,
                        width: 12,
                      }}
                    />
                    <Typography variant="body2">{label}</Typography>
                  </Stack>
                  <Typography color="text.secondary" variant="body2">
                    {formatClusterPopulationPercentage(getClusterPopulationPercentageValue(value))}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No population fractions were returned for this cluster.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const ClusterCentroidPanel: React.FC<{
  centroidValues: number[];
  clusterLabel: string;
  columnLabels: string[];
  resolveCellClassChartColor: CellClassChartColorResolver;
}> = ({ centroidValues, clusterLabel, columnLabels, resolveCellClassChartColor }) => {
  const entries = getClusterCentroidEntries(centroidValues, columnLabels, resolveCellClassChartColor);
  const chartHeight = Math.max(220, size(entries) * 36);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        height: '100%',
        p: 2,
      }}
    >
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Centroid profile</Typography>
          <Typography variant="body2" color="text.secondary">
            Relative centroid values across the same cell-label columns.
          </Typography>
        </Stack>
        {size(entries) ? (
          <Box sx={{ height: chartHeight, position: 'relative' }}>
            <Bar data={getCentroidChartData(clusterLabel, entries)} options={centroidBarChartOptions} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No centroid values were returned for this cluster.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const ClusterProfileCard: React.FC<{
  centroidValues: number[];
  clusterFractions: number[];
  clusterIndex: number;
  columnLabels: string[];
  resolveCellClassChartColor: CellClassChartColorResolver;
}> = ({ centroidValues, clusterFractions, clusterIndex, columnLabels, resolveCellClassChartColor }) => {
  const clusterLabel = `Cluster ${clusterIndex + 1}`;

  return (
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
        <Stack spacing={2.5}>
          <Stack spacing={0.5}>
            <Typography variant="h6">{clusterLabel}</Typography>
            <Typography variant="body2" color="text.secondary">
              Review the cluster composition and centroid signature together for the same cell-label columns.
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} xl={4}>
              <ClusterPopulationPanel
                clusterFractions={clusterFractions}
                clusterLabel={clusterLabel}
                columnLabels={columnLabels}
                resolveCellClassChartColor={resolveCellClassChartColor}
              />
            </Grid>
            <Grid item xs={12} xl={8}>
              <ClusterCentroidPanel
                centroidValues={centroidValues}
                clusterLabel={clusterLabel}
                columnLabels={columnLabels}
                resolveCellClassChartColor={resolveCellClassChartColor}
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>
    </Grid>
  );
};

const CellNeighborhoodsJobResults: React.FC<{ job: NeighborhoodClustersJob }> = ({ job }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { data: cellColorMappings } = useCellColorMappings();
  const { data: cellTaxonomies } = useTaxonomy(cellTaxonomy);
  const [pendingClusteringIndex, setPendingClusteringIndex] = React.useState<number | null>(null);
  const [useStudyCellClassColors, setUseStudyCellClassColors] = React.useState(false);
  const canUpdateInternalApproval = hasPermission(Permission.ExecuteNeighborhoodClusters);
  const clusteringResults = job?.results?.kMeansClusteringResults || [];
  const summary = summarizeCellNeighborhoodJob(job);
  const selectedClusteringIndex =
    pendingClusteringIndex ?? clusteringResults.findIndex((clusteringResult) => clusteringResult.internallyApproved);
  const approvedRunLabel = selectedClusteringIndex >= 0 ? `Run ${selectedClusteringIndex + 1}` : 'Not set';
  const selectedClusteringValue = selectedClusteringIndex >= 0 ? String(selectedClusteringIndex) : '';

  const updateInternalApprovalMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'update neighborhood clusters internal approval',
    mutationFn: ({ clusteringResultIndex }: { clusteringResultIndex: number }) =>
      updateNeighborhoodClustersJobInternalApproval({
        jobId: job.id,
        clusteringResultIndex,
        internallyApproved: true,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(getJobQueryKey(job.id)),
        queryClient.invalidateQueries([jobsQueryKey]),
      ]);
    },
    onSettled: () => {
      setPendingClusteringIndex(null);
    },
  });

  const handleInternalApprovalUpdate = (clusteringResultIndex: number) => {
    setPendingClusteringIndex(clusteringResultIndex);
    updateInternalApprovalMutation.mutate({ clusteringResultIndex });
  };

  const resolveCellClassChartColor = (label: string, columnIndex: number) =>
    useStudyCellClassColors
      ? getChartColorsForLabel({
          cellColorMappings,
          cellTaxonomies,
          columnIndex,
          label,
        })
      : getDefaultChartColorsForLabel(columnIndex);

  if (!size(clusteringResults)) {
    return (
      <Grid item>
        <Typography variant="body2" color="text.secondary">
          No cell neighborhood results are available yet.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      <Grid item>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Review the candidate neighborhood-clustering runs, compare cluster populations, and keep a single run marked
          as the internally approved result.
        </Typography>
        <Grid container spacing={2}>
          <SummaryMetric label="Slides in job" value={summary.slides} />
          <SummaryMetric label="Candidate runs" value={summary.clusteringRuns} />
          <SummaryMetric label="Approved run" value={approvedRunLabel} />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            alignItems={{ xs: 'flex-start', md: 'center' }}
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Chart colors</Typography>
              <Typography variant="body2" color="text.secondary">
                Use the original palette by default, or switch the full view to study cell class colors.
              </Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={useStudyCellClassColors}
                  color="primary"
                  onChange={(_event, checked) => setUseStudyCellClassColors(checked)}
                  size="small"
                />
              }
              label="Use study cell class colors"
              sx={{ m: 0 }}
            />
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            name={`approved-neighborhood-run-${job.id}`}
            onChange={(_event, value) => handleInternalApprovalUpdate(Number(value))}
            value={selectedClusteringValue}
          >
            <Grid container spacing={2}>
              {map(clusteringResults, (clusteringResult, clusteringIndex) => {
                const columnLabels = getCellNeighborhoodColumnLabels(clusteringResult);
                const clusterCount = Math.max(
                  size(clusteringResult.cellFractionsPerCluster),
                  size(clusteringResult.clusteringCentroids)
                );
                const isApprovedRun = selectedClusteringIndex === clusteringIndex;

                return (
                  <Grid item xs={12} key={`${job.id || 'job'}-clustering-result-${clusteringIndex}`}>
                    <Paper variant="outlined" sx={{ overflow: 'visible', p: { xs: 2, md: 3 } }}>
                      <Grid container spacing={2.5}>
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={(theme) => ({
                              backgroundColor: theme.palette.background.paper,
                              boxShadow: theme.shadows[1],
                              p: { xs: 2, md: 2.5 },
                              position: 'sticky',
                              top: theme.spacing(2),
                              zIndex: 2,
                            })}
                          >
                            <Grid alignItems="center" container spacing={2}>
                              <Grid item xs={12} md={8}>
                                <Stack spacing={1.5}>
                                  <Typography variant="h5">{`Clustering Run ${clusteringIndex + 1}`}</Typography>
                                  <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
                                    <Chip label={getKValueLabel(clusteringResult)} size="small" variant="outlined" />
                                    <Chip
                                      label={`${clusteringResult.neighborCountParams.nNeighbors} neighbors`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    {isApprovedRun ? (
                                      <Chip label="Approved run" size="small" color="success" variant="outlined" />
                                    ) : null}
                                  </Stack>
                                </Stack>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Stack spacing={0.75}>
                                  <Typography variant="overline" color="text.secondary">
                                    Internal approval
                                  </Typography>
                                  {canUpdateInternalApproval ? (
                                    <FormControlLabel
                                      control={
                                        <Radio
                                          color="success"
                                          disabled={updateInternalApprovalMutation.isLoading}
                                          inputProps={{
                                            'aria-label': `Use clustering run ${
                                              clusteringIndex + 1
                                            } as the approved run`,
                                          }}
                                          size="small"
                                        />
                                      }
                                      label={
                                        pendingClusteringIndex === clusteringIndex
                                          ? 'Saving selection...'
                                          : isApprovedRun
                                          ? 'Approved run'
                                          : 'Use as approved run'
                                      }
                                      labelPlacement="start"
                                      sx={{
                                        justifyContent: 'space-between',
                                        m: 0,
                                        width: '100%',
                                      }}
                                      value={String(clusteringIndex)}
                                    />
                                  ) : (
                                    <Typography variant="body2">
                                      {isApprovedRun ? 'Approved run' : 'Not approved'}
                                    </Typography>
                                  )}
                                  <Typography color="text.secondary" variant="body2">
                                    Only one clustering run can remain approved at a time.
                                  </Typography>
                                </Stack>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <KeyValueDetails
                            title="Run configuration"
                            details={[
                              { label: 'Iterations', value: clusteringResult.kMeansParams.numIterations },
                              { label: 'Restart attempts', value: clusteringResult.kMeansParams.numRedo },
                              {
                                label: 'Normalized counts',
                                value: clusteringResult.neighborCountParams.normalizeCounts ? 'Yes' : 'No',
                              },
                              {
                                label: 'Arcsinh cofactor',
                                value: clusteringResult.neighborCountParams.arcsinhNormalizationCoFactor ?? 'N/A',
                              },
                              {
                                label: 'Count column prefix',
                                value: clusteringResult.neighborCountParams.countColumnPrefix || 'N/A',
                              },
                            ]}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <KeyValueDetails
                            title="Quality summary"
                            details={[
                              {
                                label: 'Davies-Bouldin score',
                                value: formatNumber(clusteringResult.davidBouldinScore),
                              },
                              { label: 'WSS score', value: formatNumber(clusteringResult.wssScore) },
                              {
                                label: 'Cell-label columns',
                                value: size(columnLabels) ? size(columnLabels) : 'N/A',
                              },
                              {
                                label: 'Centroid rows',
                                value: size(clusteringResult.clusteringCentroids),
                              },
                              {
                                label: 'Population rows',
                                value: size(clusteringResult.cellFractionsPerCluster),
                              },
                            ]}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={0.5} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2">Per-cluster profiles</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Each cluster combines its population mix and centroid profile so you can compare both
                              views together.
                            </Typography>
                          </Stack>
                          <Grid container spacing={2}>
                            {map(range(clusterCount), (clusterIndex) => (
                              <ClusterProfileCard
                                centroidValues={clusteringResult.clusteringCentroids[clusterIndex] || []}
                                clusterFractions={clusteringResult.cellFractionsPerCluster[clusterIndex] || []}
                                clusterIndex={clusterIndex}
                                columnLabels={columnLabels}
                                resolveCellClassChartColor={resolveCellClassChartColor}
                                key={`cluster-profile-${clusteringIndex}-${clusterIndex}`}
                              />
                            ))}
                          </Grid>
                        </Grid>
                        <MatrixTable
                          title="Cluster Composition"
                          rows={clusteringResult.cellFractionsPerCluster}
                          columnLabels={columnLabels}
                          valueMode="fraction"
                        />
                        <MatrixTable
                          title="Cluster Centroids"
                          rows={clusteringResult.clusteringCentroids}
                          columnLabels={columnLabels}
                          valueMode="number"
                        />
                      </Grid>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>
        </FormControl>
      </Grid>
    </>
  );
};

const CellNeighborhoods: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  pageSize = 10,
  selectedStudyId,
}) => {
  return (
    <JobReportTab<NeighborhoodClustersJob>
      tabTitle="Cell Neighborhoods"
      jobTypes={neighborhoodJobTypes}
      emptyStateMessage="No cell neighborhood results are available yet."
      pageSize={pageSize}
      selectedStudyId={selectedStudyId}
      currentJobIdQueryParamName="jobIdForCellNeighborhoodsReportTab"
      renderSelectedJob={({ job }) => <CellNeighborhoodsJobResults job={job} />}
    />
  );
};

export default CellNeighborhoods;
